/**
 * Parent-to-dancer linking, client side.
 *
 * Bobbie, 2026-08-06: comp parents need to sign in and add their dancers so
 * comp communication can start. A parent who cannot link their dancers makes
 * signup pointless, because every one of them becomes a message to Kim.
 *
 * A CLAIM is what a parent types: their own name, and each of their kids with
 * a birthday. It is an unverified assertion and it grants NOTHING. Access to
 * a dancer lives where it always has, on students/{id}.familyUids, and the
 * only thing that ever writes it is the server after the account gate has
 * been satisfied. Nothing in this file, or anywhere else in the app, reads a
 * claim to decide what a person may see.
 *
 * Everything here is a call to /.netlify/functions/dancerClaims carrying a
 * Firebase ID token. There is no Firestore listener and no client write,
 * because the claim collection is not mentioned in shared/firestore.rules and
 * the ruleset's catch-all therefore denies it to every browser. That is
 * deliberate: the rules pipeline was frozen the night this shipped, and the
 * claim had nowhere safe to live on users/{uid} (hasUserShape() is a
 * keys().hasOnly() allowlist, so an extra field there would have denied every
 * client self-update, push-token registration included). See the header of
 * netlify/functions/dancerClaims.mjs for the full reasoning.
 */

import { getAuth } from 'firebase/auth';
import type { UserDoc, UserRole } from './users';

const CLAIMS_FN = '/.netlify/functions/dancerClaims';

/** The most dancers one family account may claim. Mirrors the server cap. */
export const MAX_CLAIMED_DANCERS = 12;
/** Nobody at the studio was born before this. Mirrors the server floor. */
export const MIN_CLAIM_DOB = '1990-01-01';

/**
 * Resolve the account the server linked to this dancer. The link lives on
 * users/{uid}.studentId and cannot be written by the client, so callers never
 * infer staff status from a matching name or from the dancer record itself.
 */
export function linkedAccountForDancer<T extends Pick<UserDoc, 'studentId' | 'role' | 'status'>>(
  studentId: string,
  users: readonly T[]
): T | null {
  return users.find((candidate) => candidate.studentId === studentId) ?? null;
}

/** Resolve one active linked account in the requested role. */
export function linkedActiveAccountForDancerRole<
  T extends Pick<UserDoc, 'studentId' | 'role' | 'status'>,
>(studentId: string, users: readonly T[], role: UserRole): T | null {
  return linkedActiveAccountsForDancerRole(studentId, users, role)[0] ?? null;
}

/** Resolve every active linked account in the requested role. */
export function linkedActiveAccountsForDancerRole<
  T extends Pick<UserDoc, 'studentId' | 'role' | 'status'>,
>(studentId: string, users: readonly T[], role: UserRole): T[] {
  return users.filter(
    (candidate) =>
      candidate.studentId === studentId &&
      candidate.status === 'active' &&
      candidate.role === role
  );
}

/** True when the dancer's server-linked account is active in this role. */
export function dancerHasLinkedRole(
  studentId: string,
  users: readonly Pick<UserDoc, 'studentId' | 'role' | 'status'>[],
  role: UserRole
): boolean {
  return linkedActiveAccountForDancerRole(studentId, users, role) !== null;
}

export type ClaimStatus = 'pending' | 'linked' | 'created' | 'needs-review';

/** One dancer as a parent types them. */
export interface DancerClaimInput {
  firstName: string;
  lastName: string;
  /** ISO YYYY-MM-DD. */
  dob: string;
  /**
   * The parent asking for this dancer to get their own sign-in one day.
   *
   * A RECORDED WISH AND NOTHING ELSE. Nothing in this build mints an account,
   * a username or a password from it. Dancer logins are a separate rebuild
   * that is on hold with unresolved security findings, so the only thing this
   * flag does is show up on the Director's card so she knows who asked.
   */
  wantsOwnLogin?: boolean;
}

/** One dancer as the server reports them back. */
export interface DancerClaimEntry extends DancerClaimInput {
  status: ClaimStatus;
  /** Set only once this entry has actually touched a dancer record. */
  studentId: string | null;
  applied: boolean;
  /**
   * True once the family has actually created this dancer's own sign-in from
   * Account. Only meaningful when wantsOwnLogin is true; false either means
   * nobody has made it yet or nobody asked. THE STALE CARD fix: before this
   * field existed, a card that saw wantsOwnLogin always said "Nothing was
   * created," forever, even after the family really made it.
   */
  loginCreated: boolean;
  /**
   * Staff view only: what the next apply would do with this entry. Never sent
   * to a family, because for an unapplied entry it predicts whether the named
   * child is on the roster. See buildClaimView.
   */
  pendingAction?: 'skip' | 'link' | 'create' | 'hold';
  /**
   * True for the legacy #482 singular claim folded in read-only. The card
   * keeps these out of its editable list so a rename cannot mint a duplicate.
   */
  fromSingular?: boolean;
  /** Plain words for this entry, already written for whoever asked. */
  summary: string;
  /** Staff view only: who the candidates are when an entry is held. */
  matchedNames?: string[];
  matchedStudentIds?: string[];
  /** Staff view only: the birthday we hold differs from the claimed one. */
  dobConflict?: boolean;
  /** Staff view only: the birthday we hold, when it differs from the claim. */
  storedDob?: string | null;
  /** Staff view only: why the last apply could not finish this entry. */
  error?: string | null;
}

/** A dancer profile this account brought into being, for the reject flow. */
export interface CreatedStudentRef {
  id: string;
  name: string;
  /**
   * True when this account is the ONLY guardian and no class has picked the
   * dancer up, which is the only case where deleting on reject is safe.
   * Anything else is flagged for a person, never auto-removed.
   */
  soleGuardian: boolean;
}

export interface DancerClaim {
  uid: string;
  parentFirstName: string;
  parentLastName: string;
  dancers: DancerClaimEntry[];
  /** True while any entry still has something waiting to happen. */
  hasUnapplied: boolean;
  /** Staff view only. Dancer profiles this account created. */
  createdStudents?: CreatedStudentRef[];
}

export interface ApplyCounts {
  linked: number;
  created: number;
  held: number;
  failed: number;
}

export interface SaveClaimResult {
  claim: DancerClaim | null;
  /** False for a pending account: stored, waiting on the studio's approval. */
  applied: boolean;
  counts?: ApplyCounts;
  message?: string;
}

/**
 * One place to call the function, so every caller gets the same auth header
 * and the same plain-words failure. Throws with the server's own message
 * when there is one, because those messages are written for the person
 * reading the screen.
 */
async function callClaims<T>(payload: Record<string, unknown>): Promise<T> {
  const me = getAuth().currentUser;
  if (!me) throw new Error('Please sign in again.');
  const idToken = await me.getIdToken();
  let response: Response;
  try {
    response = await fetch(CLAIMS_FN, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new Error('Could not reach the studio. Check your connection and try again.');
  }
  const data = (await response.json().catch(() => ({}))) as { error?: string } & T;
  if (!response.ok || data.error) {
    throw new Error(data.error || `Could not save that (${response.status}).`);
  }
  return data;
}

/** Read one claim. Your own always; a Director may read anyone's. */
export async function fetchDancerClaim(uid?: string): Promise<DancerClaim | null> {
  const data = await callClaims<{ claim: DancerClaim | null }>({ action: 'get', uid });
  return data.claim ?? null;
}

/** Read every stored claim. Director only, for the Manage Users page. */
export async function fetchAllDancerClaims(): Promise<DancerClaim[]> {
  const data = await callClaims<{ claims: DancerClaim[] }>({ action: 'list' });
  return Array.isArray(data.claims) ? data.claims : [];
}

/**
 * Read the privacy-minimized Jr Staff marker for a dancer the caller may see.
 * The server returns only a boolean, never the linked account's identity.
 */
export async function fetchDancerJrStaffStatus(studentId: string): Promise<boolean> {
  const data = await callClaims<{ isJrStaff?: boolean }>({ action: 'linked-role', studentId });
  return data.isJrStaff === true;
}

/**
 * Save my own claim. On an account the studio has already approved this also
 * applies it on the spot and tells the Director; on an account still waiting
 * for approval it only stores, and approval is what applies it.
 */
export async function saveMyDancerClaim(input: {
  parentFirstName: string;
  parentLastName: string;
  dancers: DancerClaimInput[];
}): Promise<SaveClaimResult> {
  const data = await callClaims<SaveClaimResult>({ action: 'save', ...input });
  return {
    claim: data.claim ?? null,
    applied: data.applied === true,
    counts: data.counts,
    message: data.message,
  };
}

/**
 * Run the parser against a stored claim and write what it decides.
 *
 * The Director calls this right after approving an account, which is what
 * makes her one existing tap do the linking too. It is also the retry when
 * an apply died halfway: re-running is safe, because an entry that already
 * touched a dancer is skipped forever after, so nothing is created twice and
 * a link she removed by hand is not put back.
 *
 * Refused with a plain message unless the target account is ACTIVE. That
 * refusal is the gate, and it lives on the server, not on the screen.
 */
export async function applyDancerClaim(uid?: string): Promise<{
  claim: DancerClaim | null;
  counts: ApplyCounts;
  /** Staff only: a bag group too full to take this parent, in words (bag group plan BG-3). */
  bagGroupNotice: string | null;
}> {
  const data = await callClaims<{ claim: DancerClaim | null; counts?: ApplyCounts; bagGroupNotice?: unknown }>({
    action: 'apply',
    uid,
  });
  return {
    claim: data.claim ?? null,
    counts: data.counts ?? { linked: 0, created: 0, held: 0, failed: 0 },
    bagGroupNotice: typeof data.bagGroupNotice === 'string' && data.bagGroupNotice ? data.bagGroupNotice : null,
  };
}

/** An empty row for the form to start from. */
export function emptyDancerRow(): DancerClaimInput {
  return { firstName: '', lastName: '', dob: '', wantsOwnLogin: false };
}

/**
 * Split a stored display name into first and last, so a parent who already
 * has a name on their account does not have to type it again. Everything
 * after the first space is the last name, which is right for "Mary Beth
 * Van Dyke" and wrong for nobody in a way that matters, since they can edit
 * both fields.
 */
export function splitDisplayName(displayName: string | null | undefined): {
  firstName: string;
  lastName: string;
} {
  const tidy = (displayName ?? '').trim().replace(/\s+/g, ' ');
  if (!tidy) return { firstName: '', lastName: '' };
  const cut = tidy.indexOf(' ');
  if (cut < 0) return { firstName: tidy, lastName: '' };
  return { firstName: tidy.slice(0, cut), lastName: tidy.slice(cut + 1) };
}

/** Tone for a status chip. Held entries are amber, never red: nothing is lost. */
export function claimStatusTone(status: ClaimStatus): 'ok' | 'wait' | 'hold' {
  if (status === 'linked' || status === 'created') return 'ok';
  if (status === 'pending') return 'wait';
  return 'hold';
}
